import React, { useState, useEffect, useMemo } from 'react';
import { X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const REASONS = [
  { id: 'price_too_high',  label: 'The price is too high' },
  { id: 'not_ready_yet',   label: "I'm not ready yet" },
  { id: 'need_more_info',  label: 'I need more information' },
  { id: 'other',           label: 'Other' },
];

const SESSION_KEY_PREFIX = 'mundalo_exit_survey_';

// Get or mint a session-stable random wall_id so anonymous submissions don't
// collide on the idempotency key. (Otherwise two anon users dismissing the
// same wall label would share the (None, 'price') key and only the first
// would be recorded.)
const getWallId = (reason) => {
  if (typeof window === 'undefined') return `${reason || 'default'}-server`;
  const key = `${SESSION_KEY_PREFIX}id_${reason || 'default'}`;
  let id = window.sessionStorage.getItem(key);
  if (!id) {
    id = `${reason || 'default'}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    window.sessionStorage.setItem(key, id);
  }
  return id;
};

/**
 * Mundalo - Exit-intent micro-survey.
 *
 * Renders a non-blocking overlay when the user dismisses the UpgradeWall.
 * One-tap chip auto-submits + dismisses. SessionStorage prevents re-show.
 */
export default function ExitIntentSurvey({ open, reason, onDismiss }) {
  const [submitted, setSubmitted] = useState(false);
  const [selectedReason, setSelectedReason] = useState(null);
  const [freeText, setFreeText] = useState('');
  const [showFreeText, setShowFreeText] = useState(false);
  const [offerSent, setOfferSent] = useState(false);

  // Stable session key - once submitted, never show again this session.
  const sessionKey = `${SESSION_KEY_PREFIX}${reason || 'default'}`;
  // Session-stable random wall_id (anti-collision for anonymous submissions).
  const wallId = useMemo(() => getWallId(reason), [reason]);

  useEffect(() => {
    if (open && typeof window !== 'undefined' && window.sessionStorage.getItem(sessionKey)) {
      onDismiss?.();
    }
  }, [open, sessionKey, onDismiss]);

  const submit = async (reasonId, text) => {
    if (submitted) return;
    setSubmitted(true);
    let issuedOffer = false;
    try {
      const res = await fetch(`${API_URL}/api/upgrade-insights`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          reason: reasonId,
          free_text: text || null,
          wall_id: wallId,
        }),
      });
      const data = await res.json().catch(() => ({}));
      issuedOffer = !!data?.offer_issued;
    } catch { /* swallow - survey is best-effort */ }
    if (typeof window !== 'undefined') {
      window.sessionStorage.setItem(sessionKey, '1');
    }
    if (issuedOffer) {
      // Hold the surface open so the user sees the confirmation.
      setOfferSent(true);
      return;
    }
    setTimeout(() => onDismiss?.(), 250);
  };

  const handleChip = (r) => {
    setSelectedReason(r.id);
    if (r.id === 'other') {
      setShowFreeText(true);
    } else {
      submit(r.id, null);
    }
  };

  if (!open) return null;
  if (typeof window !== 'undefined' && window.sessionStorage.getItem(sessionKey) && !offerSent) return null;

  return (
    <div
      data-testid="exit-intent-survey"
      className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[110] w-[min(560px,calc(100vw-32px))]
                 bg-[#F9F6EF] border border-[#EFE6CF] rounded-xl shadow-2xl p-5 md:p-6
                 animate-in fade-in slide-in-from-bottom-2 duration-300"
      role="dialog"
      aria-label="Why didn't you upgrade?"
    >
      <button
        onClick={() => onDismiss?.()}
        data-testid="exit-intent-close"
        className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-[#F4ECD8] transition"
        aria-label="Close survey"
      >
        <X className="w-4 h-4 text-[#0A1F2E]/60" />
      </button>

      <p className="font-mono uppercase text-[10px] tracking-[0.18em] text-[#1A5C6E] mb-1">
        Quick question · one tap
      </p>
      <h3 className="font-display italic text-xl md:text-2xl text-[#0A1F2E] leading-snug mb-4">
        What stopped you today?
      </h3>

      {offerSent ? (
        <div
          className="bg-[#FFF9EC] border border-[#E8A830] rounded-lg p-4 space-y-2"
          data-testid="exit-intent-offer-confirmation"
        >
          <p className="font-mono uppercase text-[10px] tracking-[0.16em] text-[#C4622D]">
            We heard you · 15% off coming
          </p>
          <p className="font-body text-sm text-[#0A1F2E] leading-relaxed">
            We're emailing a one-time <strong>15% off Essentials</strong> code to your inbox
            within 60 seconds. Code is valid for 48 hours.
          </p>
          <div className="flex justify-end pt-1">
            <button
              type="button"
              onClick={() => onDismiss?.()}
              data-testid="exit-intent-offer-close"
              className="bg-[#0A1F2E] hover:bg-[#0A1F2E]/90 text-[#F9F6EF] font-mono uppercase
                         text-[10px] tracking-[0.16em] font-semibold py-2 px-4 rounded-full transition"
            >
              Got it
            </button>
          </div>
        </div>
      ) : !showFreeText ? (
        <div className="flex flex-wrap gap-2" data-testid="exit-intent-chips">
          {REASONS.map((r) => (
            <button
              key={r.id}
              type="button"
              disabled={submitted}
              onClick={() => handleChip(r)}
              data-testid={`exit-intent-chip-${r.id}`}
              className={`px-3.5 py-2 rounded-full text-xs font-mono uppercase tracking-[0.1em] transition
                ${selectedReason === r.id
                  ? 'bg-[#E8A830] text-[#0A1F2E] font-semibold'
                  : 'bg-white text-[#0A1F2E] border border-[#EFE6CF] hover:border-[#E8A830] hover:bg-[#FFF9EC]'}
                disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {r.label}
            </button>
          ))}
        </div>
      ) : (
        <div className="space-y-3" data-testid="exit-intent-free-text">
          <textarea
            value={freeText}
            onChange={(e) => setFreeText(e.target.value)}
            placeholder="Tell us a little more (optional)…"
            rows={2}
            maxLength={500}
            className="w-full bg-white border border-[#EFE6CF] rounded-lg px-3 py-2 text-sm
                       font-body text-[#0A1F2E] placeholder-[#0A1F2E]/40 focus:outline-none
                       focus:border-[#E8A830] resize-none"
            data-testid="exit-intent-free-text-input"
          />
          <div className="flex justify-end">
            <button
              type="button"
              disabled={submitted}
              onClick={() => submit('other', freeText.trim() || null)}
              data-testid="exit-intent-free-text-submit"
              className="bg-[#E8A830] hover:bg-[#d59824] text-[#0A1F2E] font-mono uppercase
                         text-[10px] tracking-[0.16em] font-semibold py-2.5 px-5 rounded-full
                         transition disabled:opacity-50"
            >
              Send
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
